import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const ReadingAndAcknowledging = () => (
  <Page
    title="Reading & Acknowledging"
    intro="AmbuNet keeps a record of what you've read: announcements you
    acknowledge, Crew Vault items shared with you, policies and SOPs you mark
    as read, and PGDs you sign. This guide covers where each lives and what
    each confirmation actually means."
  >
    <Section title="Announcements">
      <p>
        <DocLink text="Announcements" url="/announcements" /> is your
        organisation's noticeboard. Some announcements require
        acknowledgement — the page counts the ones awaiting yours and has a{' '}
        <strong>Needs Acknowledgement</strong> filter, so nothing waiting on
        you gets lost. Open an announcement and acknowledge it to confirm
        you've read it; your acknowledgement is recorded with your name
        against the announcement.
      </p>
    </Section>

    <Section title="Crew Vault">
      <p>
        <DocLink text="Crew Vault" url="/crewvault" /> holds reference
        documents and notes published to crews — each item can be restricted
        by role or team, so you only see what's meant for you. Items shared
        with you that you haven't opened yet are marked{' '}
        <strong>Unread</strong> and sort to the top, and your unread count
        shows as a badge on the sidebar. Opening an item clears its unread
        mark — there's no separate acknowledgement step.
      </p>
    </Section>

    <Section title="Policy Library">
      <p>
        <DocLink text="Policy Library" url="/policy-library" /> holds your
        organisation's policies as PDF documents. Open a policy to read it
        and then use <strong>Mark as Read</strong> to record that you have.
      </p>
      <Warning title="Open the document first">
        Mark as Read stays locked until you have actually opened the policy
        document — you can't confirm reading something you haven't opened.
      </Warning>
    </Section>

    <Section title="SOPs and guidelines">
      <p>
        <DocLink
          text="Standard Operating Procedures"
          url="/standard-operating-procedures"
        />{' '}
        holds SOPs and guidelines, each as a PDF or a video. Where an SOP
        requires acknowledgement, open it and use{' '}
        <strong>Mark as Read</strong> to record yours.
      </p>
    </Section>

    <Section title="PGDs">
      <p>
        <DocLink text="PGD Library" url="/pgd-library" /> holds your
        organisation's Patient Group Directions. As crew, you only ever see
        the current PGDs — ones within their review date; out-of-date and
        archived versions are hidden from staff until governance uploads a
        reviewed version.
      </p>
      <Bullets>
        <Bullet>
          <strong>Signing</strong> confirms you have read and understood the
          PGD and will work within it. The sign button only appears for staff
          with a professional registration (body and number) recorded on
          their employee profile, and you re-enter your AmbuNet password to
          confirm the signature is really yours.
        </Bullet>
        <Bullet>
          <strong>New versions need new signatures</strong> — when a reviewed
          version is uploaded, it becomes the PGD staff sign, and the previous
          version is archived with its signatures kept on record.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Safety alerts">
      <p>
        The Safety Alerts page itself is a Governance-team worklist of MHRA
        drug and device alerts — you won't see it as crew. When an alert is
        relevant to your organisation, governance publishes it as an{' '}
        <strong>announcement requiring acknowledgement</strong>, with a link
        to the MHRA notice — so safety alerts reach you through{' '}
        <DocLink text="Announcements" url="/announcements" />, and you
        acknowledge them there like any other announcement.
      </p>
    </Section>

    <Section title="Who can do this?">
      <Info title="Who can do this?">
        Any signed-in staff member can read and acknowledge announcements,
        Crew Vault items shared with their role or team, policies, and SOPs.
        Signing a PGD needs a professional registration recorded on your
        employee profile. Publishing any of these is a governance job — see{' '}
        <DocLink
          docsPage
          url="/clinical-governance/policies-and-sops"
          text="Policies &amp; SOPs"
        />
        .
      </Info>
    </Section>
  </Page>
)

export default ReadingAndAcknowledging
